import React, { useState, useEffect, useMemo, useRef } from "react";
import { FiSearch, FiFilter, FiX } from "react-icons/fi";
import { useClients } from "@/hooks/client-profile/useClients";
import { useCohorts } from "@/hooks/cohort/useCohorts";
import { CohortService } from "@/services/cohortService";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import {
  PAYMENT_STATUS_OPTIONS,
  PaymentStatusOption,
} from "@/constants/paymentStatus";

interface PaymentStatusRow {
  cohortId: string;
  cohortName: string;
  client: {
    id: string;
    name: string;
    logo: string;
  };
  clientDetailsId: string;
  participants: number;
  currency: string;
  amountPerPerson: number;
  amountDue: number;
  paymentStatus: PaymentStatusOption;
  statusUpdated: Date;
  // Earliest session date; null when the cohort has no dated sessions.
  courseDate: Date | null;
}

const ALL = "all";

type DateField = "course" | "updated";

// "YYYY-MM-DD" from a date input, read as local midnight.
const parseInputDate = (value: string): Date | null => {
  if (!value) return null;
  const [y, m, d] = value.split("-").map(Number);
  return y && m && d ? new Date(y, m - 1, d) : null;
};

const firstSessionDate = (sessions: { date: string }[] = []): Date | null => {
  const times = sessions
    .map((s) => (s.date ? parseInputDate(s.date.slice(0, 10)) : null))
    .filter((d): d is Date => !!d && !isNaN(d.getTime()))
    .map((d) => d.getTime());
  return times.length ? new Date(Math.min(...times)) : null;
};

const PaymentStatus: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const { clients, loading: clientsLoading } = useClients();
  const { cohorts, loading: cohortsLoading } = useCohorts();
  const [paymentData, setPaymentData] = useState<PaymentStatusRow[]>([]);

  // Filter panel state. Statuses are multi-select; the rest are single-select
  // and default to "all".
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [clientFilter, setClientFilter] = useState<string>(ALL);
  const [cohortFilter, setCohortFilter] = useState<string>(ALL);
  const [currencyFilter, setCurrencyFilter] = useState<string>(ALL);
  const [dateField, setDateField] = useState<DateField>("course");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const filterRef = useRef<HTMLDivElement>(null);

  // Close the panel on an outside click or Escape.
  useEffect(() => {
    if (!isFilterOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!filterRef.current?.contains(e.target as Node)) setIsFilterOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsFilterOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isFilterOpen]);

  // Combine cohort and client data - create one row per client per cohort
  useEffect(() => {
    if (clients && cohorts) {
      const data: PaymentStatusRow[] = [];

      cohorts.forEach((cohort) => {
        // Demo cohorts are excluded from payment status entirely.
        if (cohort.isDemo === true) return;
        // For each client in the cohort, create a separate row
        cohort.selectedClients.forEach((selectedClient) => {
          const client = clients.find((c) => c.id === selectedClient.clientId);

          // Count participants from this specific client
          const clientParticipants = cohort.participants.filter(
            (p) => p.clientId === selectedClient.clientId
          ).length;

          // Calculate amount due (price per person * number of participants)
          const pricePerPerson = parseFloat(selectedClient.pricePerPerson) || 0;
          const amountDue = pricePerPerson * clientParticipants;

          data.push({
            cohortId: cohort.id,
            cohortName: cohort.name,
            client: {
              id: selectedClient.clientId,
              name: client?.name || "Unknown Client",
              logo: client?.iconLogoUrl || "",
            },
            clientDetailsId: selectedClient.id,
            participants: clientParticipants,
            currency: selectedClient.preferredCurrency || "USD",
            amountPerPerson: pricePerPerson,
            amountDue: amountDue,
            paymentStatus: (selectedClient.paymentStatus as any) || "Quoted",
            statusUpdated: cohort.updatedAt,
            courseDate: firstSessionDate(cohort.sessions),
          });
        });
      });

      setPaymentData(data);
    }
  }, [clients, cohorts]);

  // Only offer filter options that actually appear in the data.
  const filterOptions = useMemo(() => {
    const clientMap = new Map<string, string>();
    const cohortMap = new Map<string, string>();
    const currencies = new Set<string>();
    paymentData.forEach((row) => {
      clientMap.set(row.client.id, row.client.name);
      cohortMap.set(row.cohortId, row.cohortName);
      if (row.currency) currencies.add(row.currency);
    });
    const byLabel = (a: { label: string }, b: { label: string }) =>
      a.label.localeCompare(b.label);
    return {
      clients: Array.from(clientMap, ([id, label]) => ({ id, label })).sort(
        byLabel
      ),
      cohorts: Array.from(cohortMap, ([id, label]) => ({ id, label })).sort(
        byLabel
      ),
      currencies: Array.from(currencies).sort(),
    };
  }, [paymentData]);

  const toggleStatus = (status: string) =>
    setStatusFilter((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );

  const clearFilters = () => {
    setStatusFilter([]);
    setClientFilter(ALL);
    setCohortFilter(ALL);
    setCurrencyFilter(ALL);
    setDateFrom("");
    setDateTo("");
  };

  const activeFilterCount =
    (statusFilter.length > 0 ? 1 : 0) +
    (clientFilter !== ALL ? 1 : 0) +
    (cohortFilter !== ALL ? 1 : 0) +
    (currencyFilter !== ALL ? 1 : 0) +
    (dateFrom || dateTo ? 1 : 0);

  const filteredData = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const from = parseInputDate(dateFrom);
    const toDay = parseInputDate(dateTo);
    // Inclusive of the whole "to" day.
    const to = toDay
      ? new Date(toDay.getFullYear(), toDay.getMonth(), toDay.getDate() + 1)
      : null;
    return paymentData.filter((row) => {
      if (from || to) {
        const date = dateField === "course" ? row.courseDate : row.statusUpdated;
        if (!date) return false;
        if (from && date < from) return false;
        if (to && date >= to) return false;
      }
      const matchesSearch =
        !term ||
        row.client.name.toLowerCase().includes(term) ||
        row.cohortName.toLowerCase().includes(term);
      const matchesStatus =
        statusFilter.length === 0 || statusFilter.includes(row.paymentStatus);
      const matchesClient =
        clientFilter === ALL || row.client.id === clientFilter;
      const matchesCohort = cohortFilter === ALL || row.cohortId === cohortFilter;
      const matchesCurrency =
        currencyFilter === ALL || row.currency === currencyFilter;
      return (
        matchesSearch &&
        matchesStatus &&
        matchesClient &&
        matchesCohort &&
        matchesCurrency
      );
    });
  }, [
    paymentData,
    searchTerm,
    statusFilter,
    clientFilter,
    cohortFilter,
    currencyFilter,
    dateField,
    dateFrom,
    dateTo,
  ]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Quoted":
        return "text-gray-600";
      case "Invoiced no p/o":
        return "text-blue-600";
      case "Invoices with p/o":
        return "text-indigo-600";
      case "Part paid":
        return "text-yellow-600";
      case "Paid":
        return "text-green-600";
      default:
        return "text-gray-600";
    }
  };

  const handlePaymentStatusUpdate = async (
    row: PaymentStatusRow,
    newStatus: typeof PAYMENT_STATUS_OPTIONS[number]
  ) => {
    try {
      // Get the current cohort
      const cohort = cohorts?.find((c) => c.id === row.cohortId);
      if (!cohort) {
        console.error("Cohort not found");
        return;
      }

      // Update the payment status for the specific client
      const updatedClients = cohort.selectedClients.map((client) => {
        if (client.id === row.clientDetailsId) {
          return {
            ...client,
            paymentStatus: newStatus,
          };
        }
        return client;
      });

      // Update the cohort in Firestore
      await CohortService.updateCohort(row.cohortId, {
        selectedClients: updatedClients,
      });

      // Update local state
      setPaymentData((prev) =>
        prev.map((item) =>
          item.cohortId === row.cohortId &&
          item.clientDetailsId === row.clientDetailsId
            ? { ...item, paymentStatus: newStatus, statusUpdated: new Date() }
            : item
        )
      );

   
    } catch (error) {
      console.error("Error updating payment status:", error);
    }
  };

  if (clientsLoading || cohortsLoading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-8">
        <LoadingSpinner
          size="large"
          message="Loading payment status..."
          className="py-8"
        />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800">Payment Status</h1>
        <p className="text-gray-600 mt-1">
          Here's an overview of the statuses of client payments and the amounts
          due.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <div className="relative flex-1 max-w-md">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search clients"
              className="pl-10 pr-4 py-2 border rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="relative" ref={filterRef}>
            <button
              type="button"
              onClick={() => setIsFilterOpen((open) => !open)}
              aria-expanded={isFilterOpen}
              aria-haspopup="dialog"
              className={`px-4 py-2 rounded-lg flex items-center gap-2 border transition-colors ${
                activeFilterCount > 0
                  ? "border-blue-500 bg-blue-50 text-blue-700"
                  : "border-transparent text-gray-600 hover:bg-gray-100"
              }`}
            >
              <FiFilter size={18} />
              Filter
              {activeFilterCount > 0 && (
                <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-blue-600 px-1.5 text-xs font-semibold text-white">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {isFilterOpen && (
              <div
                role="dialog"
                aria-label="Filter payment status"
                className="absolute right-0 z-20 mt-2 w-80 rounded-lg border border-gray-200 bg-white p-4 shadow-lg"
              >
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-semibold text-gray-900">
                    Filters
                  </h2>
                  <button
                    type="button"
                    onClick={() => setIsFilterOpen(false)}
                    aria-label="Close filters"
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <FiX size={16} />
                  </button>
                </div>

                <fieldset className="mb-4">
                  <legend className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">
                    Payment status
                  </legend>
                  <div className="space-y-1.5">
                    {PAYMENT_STATUS_OPTIONS.map((status) => (
                      <label
                        key={status}
                        className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={statusFilter.includes(status)}
                          onChange={() => toggleStatus(status)}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        {status}
                      </label>
                    ))}
                  </div>
                </fieldset>

                <div className="mb-3">
                  <label
                    htmlFor="filter-client"
                    className="block text-xs font-medium uppercase tracking-wide text-gray-500 mb-1"
                  >
                    Client
                  </label>
                  <select
                    id="filter-client"
                    value={clientFilter}
                    onChange={(e) => setClientFilter(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={ALL}>All clients</option>
                    {filterOptions.clients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label
                    htmlFor="filter-cohort"
                    className="block text-xs font-medium uppercase tracking-wide text-gray-500 mb-1"
                  >
                    Cohort
                  </label>
                  <select
                    id="filter-cohort"
                    value={cohortFilter}
                    onChange={(e) => setCohortFilter(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={ALL}>All cohorts</option>
                    {filterOptions.cohorts.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-4">
                  <label
                    htmlFor="filter-currency"
                    className="block text-xs font-medium uppercase tracking-wide text-gray-500 mb-1"
                  >
                    Currency
                  </label>
                  <select
                    id="filter-currency"
                    value={currencyFilter}
                    onChange={(e) => setCurrencyFilter(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={ALL}>All currencies</option>
                    {filterOptions.currencies.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>

                <fieldset className="mb-4">
                  <legend className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
                    Date range
                  </legend>
                  <select
                    aria-label="Date to filter by"
                    value={dateField}
                    onChange={(e) => setDateField(e.target.value as DateField)}
                    className="w-full mb-2 rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="course">Course date</option>
                    <option value="updated">Status updated</option>
                  </select>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="text-xs text-gray-500">
                      From
                      <input
                        type="date"
                        value={dateFrom}
                        max={dateTo || undefined}
                        onChange={(e) => setDateFrom(e.target.value)}
                        className="mt-0.5 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </label>
                    <label className="text-xs text-gray-500">
                      To
                      <input
                        type="date"
                        value={dateTo}
                        min={dateFrom || undefined}
                        onChange={(e) => setDateTo(e.target.value)}
                        className="mt-0.5 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </label>
                  </div>
                </fieldset>

                <div className="flex items-center justify-between border-t border-gray-100 pt-3">
                  <span className="text-xs text-gray-500">
                    {filteredData.length} of {paymentData.length} rows
                  </span>
                  <button
                    type="button"
                    onClick={clearFilters}
                    disabled={activeFilterCount === 0}
                    className="text-sm font-medium text-blue-600 hover:text-blue-700 disabled:text-gray-300"
                  >
                    Clear all
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-4 px-4 font-medium text-gray-600">
                  Cohort
                </th>
                <th className="text-left py-4 px-4 font-medium text-gray-600">
                  Course date
                </th>
                <th className="text-left py-4 px-4 font-medium text-gray-600">
                  Client
                </th>
                <th className="text-center py-4 px-4 font-medium text-gray-600">
                  Participants
                </th>
                <th className="text-left py-4 px-4 font-medium text-gray-600">
                  Currency used
                </th>
                <th className="text-right py-4 px-4 font-medium text-gray-600">
                  Amount p/p
                </th>
                <th className="text-right py-4 px-4 font-medium text-gray-600">
                  Amount due
                </th>
                <th className="text-left py-4 px-4 font-medium text-gray-600">
                  Payment status
                </th>
                <th className="text-left py-4 px-4 font-medium text-gray-600">
                  Status updated
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length === 0 && (
                <tr>
                  <td colSpan={9} className="py-10 text-center text-gray-500">
                    No rows match the current search or filters.
                    {(activeFilterCount > 0 || searchTerm) && (
                      <button
                        type="button"
                        onClick={() => {
                          clearFilters();
                          setSearchTerm("");
                        }}
                        className="ml-2 font-medium text-blue-600 hover:text-blue-700"
                      >
                        Clear
                      </button>
                    )}
                  </td>
                </tr>
              )}
              {filteredData.map((row, index) => (
                <tr key={index} className="border-b hover:bg-gray-50">
                  <td className="py-4 px-4 text-gray-600">{row.cohortName}</td>
                  <td className="py-4 px-4 text-gray-600 whitespace-nowrap">
                    {row.courseDate ? row.courseDate.toLocaleDateString() : "—"}
                  </td>
                  <td className="py-4 px-4">
                    <span className="font-medium">{row.client.name}</span>
                  </td>
                  <td className="py-4 px-4 text-center">{row.participants}</td>
                  <td className="py-4 px-4">{row.currency}</td>
                  <td className="py-4 px-4 text-right">
                    {row.amountPerPerson.toLocaleString()}
                  </td>
                  <td className="py-4 px-4 text-right">
                    {row.amountDue.toLocaleString()}
                  </td>
                  <td className="py-4 px-4">
                    <select
                      value={row.paymentStatus}
                      onChange={(e) =>
                        handlePaymentStatusUpdate(
                          row,
                          e.target.value as typeof PAYMENT_STATUS_OPTIONS[number]
                        )
                      }
                      className={`px-3 py-1 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 ${getStatusColor(
                        row.paymentStatus
                      )} font-medium`}
                    >
                      {PAYMENT_STATUS_OPTIONS.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-4 px-4 text-gray-600">
                    {row.statusUpdated.toLocaleDateString()} at{" "}
                    {row.statusUpdated.toLocaleTimeString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PaymentStatus;
